import { IHealthFacilityData, INotSure } from "@/types";
import { ApiUrl } from "@/utils/ApiNames";
import { AppRequest } from "@/utils/AppRequest";

/**
 * @function getAssessmentData
 * @description Get assessment data
 */
const getAssessmentData = async (query = ""): Promise<INotSure> => {
  const $url = ApiUrl.kbCollect + query;

  try {
    const response = await AppRequest($url);
    return response?.data;
  } catch (error: INotSure) {
    return Promise.reject(error);
  }
};

/**
 * @function checkConditions
 * @description Check conditions
 */

const checkConditions = (data: IHealthFacilityData) => {
  const errors: string[] = [];

  // Condition 1: Assessor has visited and assessed more than three (3) HFs in a day
  // Calculated in the monitoring store

  // Condition 2: Health facility is recorded as having been assessed in Phase 1 and has been assessed in Phase 2
  if (data["group1/group_2/hf_assessdbf"]?.toLocaleLowerCase() === "yes") {
    errors.push(
      "Health facility is recorded as having been assessed in Phase 1 and has been assessed in Phase 2"
    );
  }

  // Condition 3: Form submitted by Assessor recorded as reassessment but no initial visit noted
  if (
    data["group1/group_2/type_impart"]?.toLocaleLowerCase() === "reassessment"
  ) {
    errors.push(
      "Form submitted by Assessor recorded as reassessment but no initial visit noted"
    );
  }

  // Condition 4: Referral facility/hospital is located in different state from health facility being assessed
  if (
    data["group1/group_2/state_referral"] &&
    data["group1/group_2/state_referral"]?.toLocaleLowerCase() !==
      data["group1/group_2/state"]?.toLocaleLowerCase()
  ) {
    errors.push(
      "Referral facility/hospital is located in different state from health facility being assessed"
    );
  }

  // Condition 5: Facility has more than one (> 1) medical officer/doctor
  if (parseInt(data["group1/group_3/med_doc"]) > 1) {
    errors.push("Facility has more than one (> 1) medical officer/doctor");
  }

  // Condition 6: Facility has more than seven (> 7) Nurse/Midwives
  if (parseInt(data["group1/group_3/nurse"]) > 7) {
    errors.push("Facility has more than seven (> 7) Nurse/Midwives");
  }

  // Condition 7: Facility has more than ten (>10) SCHEWs
  if (parseInt(data["group1/group_3/schew"]) > 10) {
    errors.push("Facility has more than ten (>10) SCHEWs");
  }

  // Condition 8: Facility has more than ten (>10) JCHEWs
  if (parseInt(data["group1/group_3/jchew"]) > 10) {
    errors.push("Facility has more than ten (>10) JCHEWs");
  }

  // Condition 9: Facility recorded with no (zero) delivery rooms but indicate they provide 'Labour and Delivery Services'
  const spfServices = data["group1/group_8/spf"]?.split(" ") || [];
  if (
    parseInt(data["group1/group_5/delivery_room"]) === 0 &&
    spfServices.includes("delivery")
  ) {
    errors.push(
      "Facility recorded with no (zero) delivery rooms but indicate they provide 'Labour and Delivery Services'"
    );
  }

  // Condition 10: National Grid selected as backup source of power
  if (
    data["group1/group_5/power"] === "national_grid" &&
    data["group1/group_5/backup_power"]?.includes("national_grid")
  ) {
    errors.push("National Grid selected as backup source of power");
  }

  // Condition 11: HF provides delivery services and not ANC services

  if (
    spfServices.includes("delivery") &&
    !spfServices?.includes("antenatal_care")
  ) {
    errors.push("HF provides delivery services and not ANC services");
  }

  // Condition 12: HF provides Immunization services but no client received Penta 3 in the last month
  if (
    spfServices?.includes("Rou_imm") &&
    parseInt(data["group1/group_8/sp_malaria"]) === 0
  ) {
    errors.push(
      "HF provides Immunization services but no client received Penta 3 in the last month"
    );
  }

  // Condition 13: In patient bed recorded but in patient ward is not available
  if (
    parseInt(data["group1/group_9/in_p_bed"]) > 0 &&
    parseInt(data["group1/group_5/inpatient_ward"]) === 0
  ) {
    errors.push("In patient bed recorded but in patient ward is not available");
  }

  // Condition 14: HF provides Immunization services but has no cold chain equipment (Refrigerator or Freezer for storing vaccine)
  if (
    spfServices?.includes("Rou_imm") &&
    parseInt(data["group1/group_10/cce_num"]) === 0
  ) {
    errors.push(
      "HF provides Immunization services but has no cold chain equipment (Refrigerator or Freezer for storing vaccine)"
    );
  }

  return errors;
};

const MonitoringApi = {
  getAssessmentData,
  checkConditions,
};

export default MonitoringApi;
